/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { ColDef } from 'ag-grid-community';

import {
  numberAsStringComparator,
  TEXT_WIDTH,
  TWO_DIGIT_WIDTH,
  THREE_DIGIT_WIDTH,
  cellStyleError,
  cellStyleInvalid,
} from 'components/common/Tables/CommonGridFormatters';
import { PitStrategyData, PitStrategySource } from 'models/viewModels';

// Used Record instead of enum to avoid typescript limitation with enums
// Typescript does not allow enums values to have computed values
export const PitStrategyTableColId: Record<string, string> = {
  DriverName: 'driverName',
  VehicleNumber: 'vehicleNumber',
  Lap: 'lap',
  LeaderLap: 'leaderLap',
  SMTPitStrategy: `pitStrategySourceMap.${PitStrategySource.SMT}.pitStopType`,
  PropenaltyPitStrategy: `pitStrategySourceMap.${PitStrategySource.PROPENALTY}.pitStopType`,
  PitRhoPitStrategy: `pitStrategySourceMap.${PitStrategySource.PITRHO}.pitStopType`,
};

const pitStrategyCellError = (data: PitStrategyData, colId: String) => {
  if (data.pitStrategySourceMap[PitStrategySource.PITRHO]?.isInvalid) return cellStyleInvalid;
  return data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType &&
    ((data.pitStrategySourceMap[PitStrategySource.SMT]?.pitStopType !==
      data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType &&
      colId === PitStrategyTableColId.SMTPitStrategy) ||
      (data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType &&
        data.pitStrategySourceMap[PitStrategySource.PROPENALTY]?.pitStopType !==
          data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType &&
        colId === PitStrategyTableColId.PropenaltyPitStrategy))
    ? cellStyleError
    : undefined;
};

const pitStrategyTooltipValueGetter = (data: PitStrategyData, colId: String) => {
  if (data.pitStrategySourceMap[PitStrategySource.PITRHO]?.isInvalid) return `Pit determined to be invalid.`;
  return data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType &&
    ((data.pitStrategySourceMap[PitStrategySource.SMT]?.pitStopType !==
      data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType &&
      colId === PitStrategyTableColId.SMTPitStrategy) ||
      (data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType &&
        data.pitStrategySourceMap[PitStrategySource.PROPENALTY]?.pitStopType !==
          data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType &&
        colId === PitStrategyTableColId.PropenaltyPitStrategy))
    ? `Feeds do not align. SMT: ${data.pitStrategySourceMap[PitStrategySource.SMT]?.pitStopType}, Propenalty: ${
        data.pitStrategySourceMap[PitStrategySource.PROPENALTY]?.pitStopType
      }, Verified: ${data.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType}`
    : undefined;
};

export const pitStrategyTableColDefs: ColDef[] = [
  {
    colId: PitStrategyTableColId.DriverName,
    headerName: 'Driver',
    headerTooltip: "Driver's name",
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: TEXT_WIDTH,
    flex: 1,
  },
  {
    colId: PitStrategyTableColId.VehicleNumber,
    headerName: 'Vehicle',
    headerTooltip: 'Vehicle number',
    cellClass: 'stringType',
    comparator: numberAsStringComparator,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      inRangeInclusive: true,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: TWO_DIGIT_WIDTH,
  },

  {
    colId: PitStrategyTableColId.Lap,
    headerName: 'Lap',
    headerTooltip: 'Lap when vehicle enters pit road',
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: THREE_DIGIT_WIDTH,
    sort: 'desc',
  },
  {
    colId: PitStrategyTableColId.LeaderLap,
    headerName: 'Leader Lap',
    headerTooltip: 'Leader lap when vehicle enters pit road',
    sort: 'desc',
    filter: 'agNumberColumnFilter',
    filterParams: {
      buttons: ['apply', 'reset'],
      closeOnApply: true,
      inRangeInclusive: true,
      defaultOption: 'inRange',
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: THREE_DIGIT_WIDTH,
  },
  {
    colId: PitStrategyTableColId.SMTPitStrategy,
    headerName: 'SMT Strategy',
    headerTooltip: 'SMT Pit Strategy',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: 42,
    cellStyle: (cell) => pitStrategyCellError(cell.data, PitStrategyTableColId.SMTPitStrategy),
    tooltipValueGetter: (cell) => pitStrategyTooltipValueGetter(cell.data, PitStrategyTableColId.SMTPitStrategy),
  },
  {
    colId: PitStrategyTableColId.PropenaltyPitStrategy,
    headerName: 'NASCAR Strategy',
    headerTooltip: 'Nascar Pit Strategy',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: 42,
    cellStyle: (cell) => pitStrategyCellError(cell.data, PitStrategyTableColId.PropenaltyPitStrategy),
    tooltipValueGetter: (cell) => pitStrategyTooltipValueGetter(cell.data, PitStrategyTableColId.PropenaltyPitStrategy),
  },
  {
    colId: PitStrategyTableColId.PitRhoPitStrategy,
    headerName: 'Verified Strategy',
    headerTooltip: 'Verified Pit Strategy',
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: [],
      closeOnApply: true,
      cellHeight: 25,
    },
    menuTabs: ['filterMenuTab'],
    suppressHeaderMenuButton: false,
    minWidth: 42,
  },
];
